import { isEqual, isNil, startCase } from 'lodash';
import {
  Schema,
  Dimension,
  Metric,
  Rollup,
} from './schema.model';
import { IFilterGroup, IQueryFilterWrapper, IMetricFilterGroup, IMetricFilterWrapper } from '../../interfaces/query-filter.interface';
import { asFilterObject, filterGroups, groupConnective, rootConnective, serializeFilterGroups, withImpliedConnective } from '../../utils/filter.util';
import { FilterDimension } from '../filter-dimension.model';
import { MetricFilter } from '../metric-filter.model';
import { Model } from '../model';
import { QueryFilter } from '../query-filter.model';
import { parseDate } from '../../utils/date.util';

const DEFAULT_LIMIT = 100;

export class Query extends Model {
  dataset: string;
  schema: Schema;
  dimensions: Dimension[];
  groupBy: Dimension[];
  dimensionProperties: any;
  slices: string[];
  interval: string;
  selection: any;
  providedMetricSelection: any;
  rollup: Rollup;
  metrics: Metric[];
  limit: number;
  isDirty = false;
  wasCompacted = false;
  compactedFields = new Set<string>();
  auth: string;
  globalFilters: IFilterGroup = [];
  dataSlicersFilters: IFilterGroup;
  firstDayOfWeek: string = null;

  order: string[];
  shareId: string;
  publicId: string;
  filters: IFilterGroup = [];
  metricFilters: IMetricFilterGroup = [];
  version: number;

  constructor(obj = {}, schema: Schema = null) {
    super(obj);
    this.schema = schema;
    this.dimensions = (obj['dimensions'] || []).map(d => schema.fetchDimension(d));
    if (obj['group_by']) {
      this.groupBy = obj['group_by'].map(d => schema.fetchDimension(d));
    }
    this.dimensionProperties = this.dimensionProperties || {};

    const filteredDimensionProperties = {};
    for (const dimensionName in this.dimensionProperties) {
      const dimension = schema.fetchDimension(dimensionName);
      if (dimension) {
        filteredDimensionProperties[dimensionName] = dimension.properties
          .map(property => this.dimensionProperties[dimensionName].find(dp => dp === property.name))
          .filter(Boolean);
        if (filteredDimensionProperties[dimensionName].length !== this.dimensionProperties[dimensionName].length) {
          this.wasCompacted = true;
          this.dimensionProperties[dimensionName]
            .filter(dp => !filteredDimensionProperties[dimensionName].includes(dp))
            .forEach(dp => this.addCompactedField(`${dimensionName}.${dp}`));
        }
      }
    }
    this.dimensionProperties = filteredDimensionProperties;

    if (obj['rollup'] && obj['rollup']['value']) {
      this.rollup = new Rollup(obj['rollup']['value']);
    } else {
      this.rollup = new Rollup(obj['rollup'] || 'total');
    }
    this.firstDayOfWeek = obj['first_day_of_week'];

    this.metrics = (obj['metrics'] || []).map(m => schema.fetchMetric(m));
    this.selection = this.selection || {};
    this.limit = this.limit || DEFAULT_LIMIT;
    this.auth = obj['auth'];
    this.providedMetricSelection = obj['metric_selection'];
    this.shareId = obj['share_id'];
    this.publicId = obj['public_id'];
    this.order = obj['order'] || [];

    this.order = this.order.filter(o => {
      const [orderDimension, orderDimensionProperty] = o.replace('-', '').split('.');
      const isDimensionProperty = !!orderDimensionProperty;

      const dimensionAndPropertyMatch = isDimensionProperty && this.dimensions.find(d => d?.name === orderDimension && d?.name === orderDimensionProperty);
      const dimensionPropertyMatch = isDimensionProperty && this.dimensionProperties[orderDimension]?.find(dp => dp === orderDimensionProperty);
      const onlyDimensionMatch = !isDimensionProperty && this.dimensions.find(d => d?.name === orderDimension);
      const metricMatch = !isDimensionProperty && this.metrics.find(m => m?.name === orderDimension);

      return (dimensionAndPropertyMatch || dimensionPropertyMatch || onlyDimensionMatch || metricMatch);
    });

    const compactedFilters = this.processFilters(schema, obj['filters']);
    const compactedMetricFilters = this.processMetricFilters(schema, obj['metric_filters']);
    const compactedGlobalFilters = this.processFilters(schema, obj['global_filters'], true);
    this.filters = compactedFilters.filters;
    this.metricFilters = compactedMetricFilters.filters;
    this.version = obj['version'] || 0;

    (obj['dimensions'] || []).filter(d => !schema.fetchDimension(d)).forEach(d => this.addCompactedField(d));
    (obj['metrics'] || []).filter(m => !schema.fetchMetric(m)).forEach(m => this.addCompactedField(m));
    const numDimensions = this.dimensions.length;
    const numMetrics = this.metrics.length;
    this.dimensions = this.dimensions.filter(d => !isNil(d));
    this.metrics = this.metrics.filter(m => !isNil(m));
    this.wasCompacted = numDimensions !== this.dimensions.length || numMetrics !== this.metrics.length || this.wasCompacted || compactedFilters.compact || compactedGlobalFilters.compact;
  }

  get name() {
    return `${this.dataset} query`;
  }

  clone(): Query {
    const clonedQuery = new Query(this.asJson(), this.schema);
    const globalFilters = asFilterObject(this.globalFilters, this.formatFilters(this.globalFilters));
    clonedQuery.globalFilters = this.processFilters(this.schema, globalFilters, true).filters;

    if (this.dataSlicersFilters) {
      const dataSlicersFilters = asFilterObject(this.dataSlicersFilters, this.formatFilters(this.dataSlicersFilters));
      clonedQuery.dataSlicersFilters = this.processFilters(this.schema, dataSlicersFilters, true).filters;
    }
    return clonedQuery;
  }

  equals(query: Query) {
    return isEqual(this.asJson(), query.asJson()) &&
      isEqual(this.globalFilters, query.globalFilters) &&
      isEqual(this.dataSlicersFilters, query.dataSlicersFilters);
  }

  isValid() {
    return !this.validate();
  }

  validate() {
    const message = [
      this.validateDimensions(),
      this.validateMetrics(),
      this.validateDates(),
    ].filter(Boolean);

    if (message.length) {
      return message;
    }
  }

  validateDimensions() {
    const dimensions = this.dimensions;

    if (dimensions.length) {
      if (!this.schema.hasCube(dimensions)) {
        return 'cube_incomplete';
      }
    }
  }

  validateMetrics() {
    const metrics = this.metrics;

    if (!metrics.length) {
      return 'no_metrics';
    }
  }

  validateDates() {
    if (!this.interval || this.interval === '') {
      return 'no_dates';
    }

    const dates = this.getDates();
    if (!dates) {
      return 'invalid_dates';
    }
  }

  getQueryBody(): Record<string, any> {
    const queryDimensions = this.resolvedQueryDimensions();
    const rollup = this.resolvedRollup();

    const body: Record<string, any> = {
      dimensions: queryDimensions.join(','),
      period: this.interval,
      slices: queryDimensions.map(() => '_').join(','),
      metrics: this.metrics.map(metric => metric.name).join(','),
      rollup,
    };

    if (queryDimensions[0] !== '_') {
      body['data'] = this.dataParam(queryDimensions);
    }

    const selection = this.auth && this.selection?.expressions?.length
      ? this.selection
      : this.createSelection(this.filters, this.globalFilters);
    if (selection.expressions.length) {
      body['selection'] = selection;
    }

    if (this.dataSlicersFilters?.length > 0) {
      body['data_slicers_selection'] = this.createSelection(this.dataSlicersFilters);
    }

    const metricSelection = this.auth && this.providedMetricSelection?.expressions?.length
      ? this.providedMetricSelection
      : this.createSelection(this.metricFilters);
    if (metricSelection.expressions.length) {
      body['metric_selection'] = metricSelection;
    }

    if (this.order.filter(Boolean).length) {
      body['order'] = this.order.join(',');
    }

    if (this.limit) {
      body['limit'] = this.limit;
    }

    if (this.auth) {
      body['auth'] = this.auth;
    }

    if (this.shareId) {
      body['share_id'] = this.shareId;
    }

    if (this.publicId) {
      body['public_id'] = this.publicId;
    }

    return body;
  }

  getSlicesBody(filterDimensions?: FilterDimension[]): Record<string, any> {
    let queryDimensions = this.dimensions.map(dimension => dimension.name);
    if (!queryDimensions.length) {
      queryDimensions = ['_'];
    }

    let dimensionSlices: string;
    if (filterDimensions?.length) {
      dimensionSlices = queryDimensions.map(dim => {
        const dimension = filterDimensions.find(fd => fd.name === dim);
        return dimension ? dimension.value : '_';
      }).join(',');
    } else {
      dimensionSlices = queryDimensions.map(() => '_').join(',');
    }

    const body: Record<string, any> = {
      dimensions: queryDimensions.join(','),
      period: this.interval,
      slices: dimensionSlices,
      rollup: 'total',
    };

    if (queryDimensions[0] !== '_') {
      body['data'] = this.dataParam(queryDimensions);
    }

    const selection = this.createSelection(this.filters, this.globalFilters);
    if (selection.expressions.length) {
      body['selection'] = selection;
    }

    if (this.dataSlicersFilters?.length > 0) {
      body['data_slicers_selection'] = this.createSelection(this.dataSlicersFilters);
    }

    if (this.limit) {
      body['limit'] = this.limit;
    }

    if (this.auth) {
      body['auth'] = this.auth;
    }

    return body;
  }

  private resolvedQueryDimensions(): string[] {
    const dimensions = [...(this.groupBy || this.dimensions)]
      .filter(Boolean)
      .sort((a, b) => this.dimensions.findIndex(d => d === a) - this.dimensions.findIndex(d => d === b))
      .map(dimension => dimension.name);
    return dimensions.length ? dimensions : ['_'];
  }

  private resolvedRollup(): string {
    return this.rollup.value === 'week' && this.firstDayOfWeek
      ? `week:${this.firstDayOfWeek}`
      : this.rollup.value;
  }

  private dataParam(queryDimensions) {
    let datas = queryDimensions.map(dimension => `${dimension}.${dimension}`);
    for (const dimensionName in this.dimensionProperties) {
      for (const propertyName of this.dimensionProperties[dimensionName]) {
        datas.push(`${dimensionName}.${propertyName}`);
      }
    }
    return datas.join(',');
  }

  private createSelection(filters: (IFilterGroup | IMetricFilterGroup), globalFilters: IFilterGroup = []) {
    const dashboardGroups = globalFilters.filter(group => group.pageId === undefined);
    const pageGroups = globalFilters.filter(group => group.pageId !== undefined);
    const trees = [
      this.selectionTree(filters),
      this.selectionTree(withImpliedConnective(dashboardGroups)),
      this.selectionTree(withImpliedConnective(pageGroups)),
    ].filter(Boolean);
    if (!trees.length) {
      return { type: rootConnective(filters), expressions: [] };
    }
    return trees.length === 1 ? trees[0] : { type: 'and', expressions: trees };
  }

  private selectionTree(groups: (IFilterGroup | IMetricFilterGroup)) {
    const expressions = groups
      .map(group => ({
        type: groupConnective(group),
        expressions: group.expressions
          .map((expression: QueryFilter | MetricFilter) => expression.analyticsPayload())
          .filter(Boolean),
      }))
      .filter(group => group.expressions.length);
    if (!expressions.length) {
      return null;
    }
    return { type: rootConnective(groups), expressions };
  }

  getSearchBody(dimension: Dimension, term: string, limitValue: number = 20, data: string[] = []): Record<string, any> {
    const body: Record<string, any> = {
      dimension: dimension.name,
      period: this.interval,
      property: dimension.name,
      term: term ?? '',
      limit: limitValue,
    };

    if (data?.length) {
      body['data'] = data.join(',');
    }

    if (this.auth) {
      body['auth'] = this.auth;
    }

    return body;
  }

  getDates() {
    if (this.interval) {
      const [startDate, endDate] = this.interval.split('-');
      if (startDate && endDate) {
        return {
          startDate: parseDate(startDate),
          endDate: parseDate(endDate),
        };
      }
    }
  }

  get hasFilters(): boolean {
    return !!this.filters.length;
  }

  get hasMetricFilters(): boolean {
    return !!this.metricFilters.length;
  }

  asJson() {
    const obj = {
      dataset: this.dataset,
      dimensions: this.dimensions.map(d => d.name),
      dimension_properties: this.dimensionProperties,
      slices: this.slices,
      interval: this.interval,
      selection: this.selection,
      rollup: this.rollup.value,
      first_day_of_week: this.firstDayOfWeek,
      metrics: this.metrics.map(m => m.name),
      order: this.order,
      limit: this.limit,
      share_id: this.shareId,
      public_id: this.publicId,
      auth: this.auth,
      filters: asFilterObject(this.filters, this.formatFilters(this.filters)),
      metric_filters: asFilterObject(this.metricFilters, this.formatMetricFilters(this.metricFilters)),
      version: this.version,
    };

    if (this.groupBy) {
      obj['group_by'] = this.groupBy.filter(Boolean).map(g => g.name);
    }

    if (this.providedMetricSelection) {
      obj['metric_selection'] = this.providedMetricSelection;
    }

    return obj;
  }

  public processFilters(schema: Schema, filters: any = [], isGlobal?: boolean) {
    let compact = false;
    const groups = filterGroups(filters);
    let finalFilters: IFilterGroup = groups.map((filter: IQueryFilterWrapper) => {
      const processedFilter: IQueryFilterWrapper = {
        expressions: filter.expressions.map(expression => {
          const dimensionProperty = expression.dimensionProperty || expression['dimension_property'];

          if (dimensionProperty) {
            const dimension = schema.fetchDimension(expression.dimension?.name);
            const property = dimension?.properties?.find(p => p.name === dimensionProperty.name);
            const isStalePropertyReference = !property && expression.key?.includes('.');

            if (isStalePropertyReference) {
              const transformedName = expression.key.replace('.', '_');
              const propertyName = expression.key.split('.')[1];

              let newDimension = schema.dimensions.find(dim => dim.name === transformedName);
              let newKey = `${transformedName}.${transformedName}`;

              if (!newDimension) {
                newDimension = schema.dimensions.find(dim => dim.name === propertyName);
                newKey = `${propertyName}.${propertyName}`;
              }

              if (newDimension) {
                compact = true;
                return new QueryFilter({
                  ...expression,
                  key: newKey,
                  dimension: newDimension,
                  dimension_property: null,
                });
              }
            }
          }

          if (!expression.dimension) {
            const dimension = schema.dimensions.find(dim => dim.name === expression.key);
            return new QueryFilter({ ...expression, dimension });
          } else {
            return new QueryFilter(expression);
          }
        }).filter(expression => {
          const dimension = schema.fetchDimension(expression.dimension?.name || expression.key);

          if (!dimension) {
            compact = true;
            this.addCompactedField(expression.dimension?.name || expression.key);
          }
          return !!dimension;
        }),
      };

      processedFilter.connective = groupConnective(filter);

      if (isGlobal) {
        processedFilter.dataset = filter.dataset;
        processedFilter.pageId = filter.pageId;
      }

      return processedFilter;
    }).filter(filter => filter.expressions.length);
    finalFilters.connective = rootConnective(filters);
    return { filters: finalFilters, compact };
  }

  public processMetricFilters(schema: Schema, metricFilters: any = []) {
    let compact = false;
    const groups = filterGroups<IMetricFilterWrapper>(metricFilters);
    let finalFilters: IMetricFilterGroup = groups.map((filter: IMetricFilterWrapper) => {
      const processedFilter: IMetricFilterWrapper = {
        expressions: filter.expressions.map(expression => {
          if (!expression.metric) {
            const metric = schema.metrics.find(dim => dim.name === expression.key);
            return new MetricFilter({ ...expression, metric });
          } else {
            return new MetricFilter(expression);
          }
        }).filter(expression => {
          const metric = schema.fetchMetric(expression.metric?.name || expression.key);
          if (!metric) {
            compact = true;
            this.addCompactedField(expression.metric?.name || expression.key);
          }
          return !!metric;
        }),
      };

      processedFilter.connective = groupConnective(filter);

      return processedFilter;
    }).filter(filter => filter.expressions.length);
    finalFilters.connective = rootConnective(metricFilters);
    return { filters: finalFilters, compact };
  }

  private addCompactedField(name: string) {
    this.compactedFields.add(startCase(name));
  }

  private formatFilters(filters) {
    return serializeFilterGroups(filters);
  }

  private formatMetricFilters(filters) {
    return filters.map((filter: IMetricFilterWrapper) => ({
      ...filter,
      expressions: filter.expressions.map(expression => expression.asJson()),
    }));
  }
}
